import parseJwt from '../../utils/jwt-parser';
import { isLocalDevelopment } from '../../config/local.config';

import {getFederateConfig} from './Config'

/**
 * Security Note: This class handles authentication tokens and URL parameters.
 * All user input is validated and sensitive data is redacted from logs to prevent XSS and data exposure.
 */

export default class TokenRetriever {

    // idPURL: string;
    // idPAuthorizeEndpoint: string;
    stage: string;
    idpConfig: {idPAuthorizeEndpoint: string, clientId:string};
    readonly ID_TOKEN_PROP: string;
    readonly ID_TOKEN_EXP_PROP: string;
    private enableLogging: boolean = false; // Default logging is disabled

    // Method to toggle logging on/off
    public toggleLogging(enable?: boolean): void {
        if (enable !== undefined) {
            this.enableLogging = enable;
        } else {
            this.enableLogging = !this.enableLogging;
        }
        console.log(`Page logging ${this.enableLogging ? 'enabled' : 'disabled'}`);
    }

    // Create a method to display logs on page (can be toggled on/off)
    private displayLogOnPage(message: string): void {
        console.log(message); // Always log to console
        
        // Skip DOM manipulation if logging is disabled
        if (!this.enableLogging) {
            return;
        }
        
        // Create log container if it doesn't exist
        let logContainer = document.getElementById('fc-bookmarks-log-container');
        if (!logContainer) {
            logContainer = document.createElement('div');
            logContainer.id = 'fc-bookmarks-log-container';
            logContainer.style.position = 'fixed';
            logContainer.style.bottom = '10px';
            logContainer.style.left = '10px';
            logContainer.style.right = '10px';
            logContainer.style.maxHeight = '200px';
            logContainer.style.overflowY = 'auto';
            logContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
            logContainer.style.color = 'white';
            logContainer.style.padding = '10px';
            logContainer.style.borderRadius = '5px';
            logContainer.style.fontFamily = 'monospace';
            logContainer.style.zIndex = '9999';
            document.body.appendChild(logContainer);
        }
        
        // Create log entry
        const logEntry = document.createElement('div');
        logEntry.style.marginBottom = '5px';
        logEntry.style.borderBottom = '1px solid #444';
        logEntry.style.paddingBottom = '5px';
        
        // Add timestamp safely using DOM methods instead of innerHTML
        const timestamp = new Date().toLocaleTimeString();
        const timestampSpan = document.createElement('span');
        timestampSpan.style.color = '#aaa';
        timestampSpan.textContent = `[${timestamp}] `;
        
        // Add message safely using textContent to prevent XSS
        const messageSpan = document.createElement('span');
        messageSpan.textContent = message;
        
        // Append both elements to the log entry
        logEntry.appendChild(timestampSpan);
        logEntry.appendChild(messageSpan);
        
        // Add to container
        logContainer.appendChild(logEntry);
        
        // Scroll to bottom to show latest logs
        logContainer.scrollTop = logContainer.scrollHeight;
    }

    /**
     * Extract and validate auth_provider parameter from the current URL query string
     * @returns {string|null} The validated auth_provider value or null if not found/invalid
     */
    private extractAuthProvider(): string | null {
        const urlParams = new URLSearchParams(window.location.search);
        const authProvider = urlParams.get('auth_provider');
        
        if (authProvider) {
            // Validate auth_provider to prevent injection attacks
            const validAuthProviders = ['authgard', 'authgard_beta', 'kerb'];
            const sanitizedProvider = authProvider.trim().toLowerCase();
            
            if (validAuthProviders.includes(sanitizedProvider)) {
                this.displayLogOnPage(`Found valid auth_provider parameter: ${sanitizedProvider}`);
                return sanitizedProvider;
            } else {
                this.displayLogOnPage(`Invalid auth_provider parameter rejected: ${authProvider}`);
                return null;
            }
        }
        
        return null;
    }

    constructor(stage: string) {

        // this.idPURL = configuration.idPURL;
        // this.idPAuthorizeEndpoint = configuration.idPAuthorizeEndpoint;
        this.stage = stage
        this.idpConfig = getFederateConfig(stage)
        this.ID_TOKEN_PROP = "federateIdToken"
        this.ID_TOKEN_EXP_PROP = "federateIdTokenExpireTimestamp"
    }

    getTokenOrRedirect() {
    // static getTokenOrRedirect() {
        return new Promise((resolve) => {
            // In local development, return a mock token
            if (isLocalDevelopment()) {
                // Return a properly formatted mock JWT token
                const mockJwtPayload = {
                    sub: 'localuser',
                    DISPLAY_NAME: 'Local, Development User',
                    BUILDING_CODE: 'LOCAL',
                    MANAGER_LOGIN: 'manager',
                    exp: Math.floor(Date.now() / 1000) + 3600 // 1 hour from now
                };
                const mockToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.' + 
                    btoa(JSON.stringify(mockJwtPayload)) + 
                    '.mock-signature';
                this.displayLogOnPage('Using mock JWT token for local development');
                resolve(mockToken);
                return;
            }

            // check local storage first
            const cachedToken = localStorage.getItem(this.ID_TOKEN_PROP);
            const expireTimestamp = localStorage.getItem(this.ID_TOKEN_EXP_PROP)
            if (cachedToken && expireTimestamp && parseInt(expireTimestamp, 10) * 1000 > Date.now()) {
                this.displayLogOnPage(`getTokenOrRedirect: local token ${this.ID_TOKEN_PROP} still usable: [CACHED_TOKEN]`);
                resolve(cachedToken);
            } else {
                const queryParams = new URLSearchParams(window.location.hash.slice(1));
                if (!queryParams.get('id_token')) {
                    // Display logs on page instead of console.log or alert
                    this.displayLogOnPage(`getTokenOrRedirect: redirect to auth`);
                    
                    // Process query parameters
                    const paramsObj: Record<string, string> = {};
                    queryParams.forEach((value, key) => {
                        paramsObj[key] = value;
                    });
                    
                    // Show parameters on page instead of alert (redact sensitive data)
                    const paramCount = Object.keys(paramsObj).length;
                    const paramsContent = paramCount > 0 
                        ? `Found ${paramCount} parameters [REDACTED_FOR_SECURITY]`
                        : "No parameters found in URL hash";
                    
                    this.displayLogOnPage(`URL Parameters: ${paramsContent}`);
                    
                    // Add a small delay to ensure logs are visible before redirect
                    setTimeout(() => {
                        window.location.href = this.buildAuthorizeUrl();
                    }, 2000);
                } else {
                    const token = queryParams.get('id_token');
                    // Ensure token is not null before parsing
                    if (token === null) {
                        throw new Error('Token is null');
                    }
                    const {exp} = parseJwt(token);
                    // Display logs on page instead of console (avoid logging sensitive token data)
                    this.displayLogOnPage(`getTokenOrRedirect got new token: [TOKEN_RECEIVED]`);
                    localStorage.setItem(this.ID_TOKEN_PROP, token);
                    localStorage.setItem(this.ID_TOKEN_EXP_PROP, exp.toString());
                    this.displayLogOnPage(`getTokenOrRedirect: new token saved locally`);
                    resolve(token);
                }
            }
        });
    }

    buildAuthorizeUrl() {
        const queryParams: Record<string, string> = {
            // client_id: encodeURIComponent(window.location.host),
            client_id: this.idpConfig.clientId,
            redirect_uri: encodeURIComponent(this.buildRedirectUri()),
            response_type: 'id_token',
            scope: 'openid',
            nonce: generateNonce(),
        };

        // Extract auth_provider from the incoming request URL and add it to Federate request
        const authProvider = this.extractAuthProvider();
        if (authProvider) {
            queryParams.auth_provider = authProvider;
            this.displayLogOnPage(`Adding auth_provider to Federate request: ${authProvider}`);

        };

        return `${this.idpConfig.idPAuthorizeEndpoint + '/api/oauth2/v1/authorize' + this.buildQuery(queryParams)}`;
    }

    buildRedirectUri() {

        // window.location.origin can fail on IE
        this.displayLogOnPage(`buildRedirectUri: window.location.host-> ${window.location.host} window.location.port-> ${window.location.port}`);
        return `${window.location.protocol}//${window.location.host}`;
        // return `${window.location.protocol}//${window.location.host + (window.location.port ? ':' + window.location.port : '')}/`;
    }

    buildQuery(parameters: Record<string, string>) {
        return Object.keys(parameters).reduce((accumulator, key) => `${accumulator + key}=${parameters[key]}&`, '?');
    }

    // getIdPURL() {
    //     return this.idPURL;
    // }
}

function generateNonce() {
    let nonce = '';
    const characterSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < 64; i += 1) {
        nonce += characterSet.charAt(Math.floor(Math.random() * characterSet.length));
    }
    return nonce;
}
